import React, { useEffect, useRef, useCallback, useState } from 'react';
import { 
  Box, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Checkbox,
  Typography,
  useTheme,
  TableSortLabel,
  alpha,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  useMediaQuery,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider
} from '@mui/material';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useAppDispatch, useAppSelector } from 'src/store';
import { toggleOpenConversation, deleteConversations } from 'src/features/account/conversations.slice';
import { selectAllChannels } from 'src/features/account/channels.slice';
import ConversationRowItem from './ConversationRowItem';
import { useSnackbar } from 'notistack';
import { Schedule, Person, ChatBubbleOutline, Subject, Visibility, QuestionAnswer, PersonOutline, Update, StarBorder, Email, AlternateEmail, FilterList, Sms, Computer, Clear } from '@mui/icons-material';

interface ConversationsTableProps {
  conversations: any[];
  handleConversationsListToggleClick: () => void;
  selectedConversations: string[];
  onSelectConversation: (conversationId: string, event?: React.MouseEvent) => void;
  showSelectionColumn: boolean;
  initialSortField?: string;
  initialSortOrder?: 'asc' | 'desc';
  loadingFullDataIds?: Set<string>;
  channels?: any[];
  mobileSelectMode?: boolean;
  onLoadMore?: () => Promise<void>;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onTagsFilterChange?: (tags: string[]) => void;
  selectedTagsFilter?: string[];
}

const ConversationsTable: React.FC<ConversationsTableProps> = ({
  conversations,
  handleConversationsListToggleClick,
  selectedConversations = [],
  onSelectConversation,
  showSelectionColumn = false,
  initialSortField = 'updated_at',
  initialSortOrder = 'desc',
  loadingFullDataIds,
  channels = [],
  mobileSelectMode = false,
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
  onTagsFilterChange,
  selectedTagsFilter = []
}) => {
  const theme = useTheme();
  const darkMode = theme.palette.mode === 'dark';
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm')); // Match ConversationRowItem's mobile detection
  const shouldVirtualize = isDesktop;
  const openConversationId = useAppSelector(state => state.conversations.openConversationId);
  const allChannels = useAppSelector(selectAllChannels);
  const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc'>(initialSortOrder);
  const [sortField, setSortField] = React.useState<string>(initialSortField);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  
  // Communication method filter state
  const [selectedMethods, setSelectedMethods] = useState<string[]>([]);
  const [methodFilterAnchor, setMethodFilterAnchor] = useState<null | HTMLElement>(null);

  // Tags filter state
  const [selectedTags, setSelectedTags] = useState<string[]>(selectedTagsFilter);
  const [tagsFilterAnchor, setTagsFilterAnchor] = useState<null | HTMLElement>(null);

  // Update tags and notify parent
  const handleTagsFilterChange = (tags: string[]) => {
    setSelectedTags(tags);
    if (onTagsFilterChange) {
      onTagsFilterChange(tags);
    }
  };
  
  // Refs for virtualization
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const estimateRowHeight = useCallback(() => (isDesktop ? 72 : 128), [isDesktop]);

  // Extract communication methods from a conversation
  const getConversationMethods = (conv: any): string[] => {
    const methods = new Set<string>();
    const userEntities = (conv.participating_entities || []).filter((e: any) => 
      e.role === 'user' && e.identifier
    );
    
    userEntities.forEach((entity: any) => {
      const identifier = entity.identifier || '';
      
      // Check for email
      if (identifier.includes('@') && !identifier.includes('sms:') && !identifier.includes('phone:')) {
        methods.add('email');
      }
      
      // Check for SMS/phone
      if (identifier.startsWith('+') || identifier.includes('sms:') || identifier.includes('phone:')) {
        methods.add('sms');
      }
      
      // Check for chat
      if (identifier.includes('ui_chat:') || identifier.includes('live_chat:')) {
        methods.add('chat');
      }
    });
    
    return Array.from(methods);
  };

  // Get all available tags from conversations
  const availableTags = React.useMemo(() => {
    const tagsSet = new Set<string>();
    conversations.forEach(conv => {
      if (conv.tags && Array.isArray(conv.tags)) {
        conv.tags.forEach((tag: string) => {
          tagsSet.add(tag.toLowerCase());
        });
      }
    });
    return Array.from(tagsSet).sort();
  }, [conversations]);

  // Create a memoized list that reflects the current sort field & order
  const sortedConversations = React.useMemo(() => {
    let convs = [...conversations];

    // Apply method filter if any methods are selected
    if (selectedMethods.length > 0) {
      convs = convs.filter(conv => {
        const convMethods = getConversationMethods(conv);
        // Check if conversation has at least one of the selected methods
        return selectedMethods.some(method => convMethods.includes(method));
      });
    }

    // Apply tags filter if any tags are selected
    if (selectedTags.length > 0) {
      convs = convs.filter(conv => {
        const convTags = (conv.tags || []).map((tag: string) => tag.toLowerCase());
        // Check if conversation has at least one of the selected tags
        return selectedTags.some(tag => convTags.includes(tag.toLowerCase()));
      });
    }

    const getComparableValue = (conv: any, field: string) => {
      if (field === 'channel_id') {
        const channel = allChannels.find(ch => ch.id === conv.channel_id);
        return channel?.chatbot_name || '';
      }
      
      const value = conv[field];
      // Convert dates to timestamps for comparable numbers
      if ([
        'updated_at', 
        'created_at', 
        'last_user_message_received_at', 
        'latest_message_at',
        'latest_delivery_at'
      ].includes(field) && value) {
        return new Date(value).getTime();
      }
      return value ?? ''; // fallback for undefined/null
    };

    convs.sort((a, b) => {
      const aVal: any = getComparableValue(a, sortField);
      const bVal: any = getComparableValue(b, sortField);

      // Handle boolean comparison (for starred)
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      }

      // Handle string comparison in a locale aware manner
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        const comparison = aVal.localeCompare(bVal);
        return sortOrder === 'asc' ? comparison : -comparison;
      }

      // Fallback: keep original order
      return 0;
    });

    return convs;
  }, [conversations, sortField, sortOrder, allChannels, selectedMethods, selectedTags]);

  // Set up the virtualizer
  const virtualizer = useVirtualizer({
    count: sortedConversations.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: estimateRowHeight,
    overscan: 5, // Number of items to render outside visible area
  });

  const virtualItems = shouldVirtualize ? virtualizer.getVirtualItems() : [];

  // Add ref to track if we're currently loading more
  const isLoadingMoreRef = useRef(false);

  // Handle scroll events for infinite scrolling
  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current || !onLoadMore || !hasMore) return;
    
    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
    const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
    
    // Trigger load more when within 100px of bottom
    if (distanceFromBottom < 100 && !isLoadingMoreRef.current && !isLoadingMore) {
      isLoadingMoreRef.current = true;
      onLoadMore().finally(() => {
        isLoadingMoreRef.current = false;
      });
    }
  }, [onLoadMore, hasMore, isLoadingMore]);

  // Centralised sort handler so clicking a column toggles direction when re-clicked
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // Effect to handle virtualizer measurements
  useEffect(() => {
    if (!shouldVirtualize) return;
    virtualizer.measure();
  }, [sortedConversations.length, shouldVirtualize, virtualizer]);

  const handleConfirmDelete = () => {
    if (!selectedConversations.length) return;
    
    dispatch(deleteConversations(selectedConversations))
      .then((result) => {
        // Use type assertion to define the expected payload structure
        interface DeleteResult {
          successfulDeletes: string[];
          failedDeletes: string[];
        }
        
        // Assert the payload type
        const payload = result.payload as DeleteResult;
        
        if (payload?.successfulDeletes?.length > 0) {
          enqueueSnackbar(`Deleted ${payload.successfulDeletes.length} conversations`, { variant: 'success' });
        }
        if (payload?.failedDeletes?.length > 0) {
          enqueueSnackbar(`Failed to delete ${payload.failedDeletes.length} conversations`, { variant: 'error' });
        }
      })
      .catch(() => {
        enqueueSnackbar('Failed to delete conversations', { variant: 'error' });
      });
    
    setDeleteDialogOpen(false);
  };

  const handleSelectConversation = (conversationId: string) => {
    // Only toggle the open conversation, don't trigger auto-selection
    dispatch(toggleOpenConversation(conversationId));
  };

  return (
    <Box sx={{
      width: '100%',
      flex: 1,
      minHeight: 0,
      height: '100%',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {selectedConversations.length} conversations? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <TableContainer
        component={Box}
        ref={scrollContainerRef}
        onScroll={handleScroll}
        sx={{
          height: '100%',
          width: '100%',
          maxWidth: '100%',
          overflowY: 'auto',
          overflowX: { xs: 'hidden', sm: 'auto' },
          boxShadow: 'none !important',
          border: 'none !important',
          borderTop: 'none !important',
          borderBottom: 'none !important',
          borderLeft: 'none !important',
          borderRight: 'none !important',
          position: 'relative',
          borderRadius: 0,
          WebkitOverflowScrolling: 'touch',
          // Make participant avatars monochrome within the conversations table only
          '& .MuiAvatarGroup-root .MuiAvatar-root': {
            filter: 'grayscale(100%)',
            backgroundColor: (theme) =>
              theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[200],
            color: (theme) =>
              theme.palette.mode === 'dark' ? theme.palette.grey[100] : theme.palette.grey[800],
            boxShadow: 'none',
            border: (theme) => `1px solid ${theme.palette.divider} !important`,
          },
          '& .MuiPaper-root': {
            boxShadow: 'none !important',
            border: 'none !important',
          },
          // Responsive table handling
          '& .MuiTable-root': {
            width: isDesktop ? 'max-content' : '100%', // Full width on mobile
            minWidth: '100%',
            maxWidth: '100%',
            tableLayout: isDesktop ? 'fixed' : 'auto', // Auto layout on mobile
            borderSpacing: 0,
          },
          '& .MuiTableBody-root': {
            '& .MuiTableRow-root': {
              // Rows already have their own styling from ConversationRowItem
            }
          },
          '& .MuiTableCell-root': {
            border: 'none !important',
            borderBottom: 'none !important',
            borderTop: 'none !important',
            borderLeft: 'none !important',
            borderRight: 'none !important',
            maxWidth: '100%',
            '&:last-child': {
              pr: { xs: 0, md: 2 },
            },
            '&:first-of-type': {
              pl: 0,
            }
          },
          // Custom scrollbar styles
          scrollbarWidth: 'thin',
          msOverflowStyle: 'auto',
          '&::-webkit-scrollbar': {
            width: '10px',
            height: '10px'
          },
          '&::-webkit-scrollbar-track': {
            bgcolor: theme.palette.background.overlay,
            borderRadius: '10px'
          },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: theme.palette.background.overlayMedium,
            borderRadius: '10px',
            transition: 'all 0.3s ease',
            '&:hover': {
              bgcolor: theme.palette.background.overlayStrong
            }
          }
        }}
      >
        <Table stickyHeader size="small" aria-label="conversations table" sx={{ 
          backgroundColor: 'transparent',
          '& .MuiTableCell-root': {
            border: 'none !important',
            borderBottom: 'none !important',
            borderTop: 'none !important',
            borderLeft: 'none !important',
            borderRight: 'none !important'
          }
        }}>
          {isDesktop && (
            <colgroup>
              <col style={{ width: '48px' }} />
              <col style={{ width: '48px' }} />
              <col style={{ width: '400px' }} />
              <col style={{ width: '80px' }} />
              <col style={{ width: '200px' }} />
              <col style={{ width: '150px' }} />
              <col style={{ width: '400px' }} />
              <col style={{ width: '60px' }} />
              <col style={{ width: '120px' }} />
              <col style={{ width: '100px' }} />
            </colgroup>
          )}
          <TableHead sx={{ bgcolor: theme.palette.background.glass, display: isDesktop ? 'table-header-group' : 'none' }}>
            <TableRow sx={{ height: '28px' }}>
              {/* Desktop headers with enhanced styling */}
              {/* Checkbox Column Header - FIRST */}
              <TableCell 
                sx={{ 
                  width: '48px',
                  minWidth: '48px',
                  maxWidth: '48px',
                  bgcolor: darkMode ? 'grey.900' : 'grey.50',
                  fontWeight: 500,
                  fontSize: '0.65rem',
                  textTransform: 'uppercase',
                  color: darkMode ? 'grey.500' : 'grey.600',
                  border: 'none',
                  borderBottom: 'none !important',
                  padding: '2px 4px',
                  textAlign: 'center',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  display: 'table-cell',
                  lineHeight: 1,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {showSelectionColumn && (
                    <Checkbox 
                      size="small"
                      indeterminate={selectedConversations.length > 0 && selectedConversations.length < conversations.length}
                      checked={conversations.length > 0 && selectedConversations.length === conversations.length}
                      onChange={(event) => {
                        if (event.target.checked) {
                          const allConversationIds = conversations.map(conv => conv.id);
                          allConversationIds.forEach(id => {
                            if (!selectedConversations.includes(id)) {
                              onSelectConversation(id);
                            }
                          });
                        } else {
                          selectedConversations.forEach(id => {
                            onSelectConversation(id);
                          });
                        }
                      }}
                      sx={{
                        p: 0,
                        color: darkMode ? 'grey.500' : 'grey.600',
                        '&.Mui-checked, &.MuiCheckbox-indeterminate': {
                          color: 'primary.main',
                        }
                      }}
                    />
                  )}
                </Box>
              </TableCell>
              
              {/* Star/Favorite Column Header - SECOND */}
              <TableCell 
                sx={{ 
                  width: '48px',
                  minWidth: '48px',
                  maxWidth: '48px',
                  bgcolor: darkMode ? 'grey.900' : 'grey.50',
                  fontWeight: 500,
                  fontSize: '0.65rem',
                  textTransform: 'uppercase',
                  color: darkMode ? 'grey.500' : 'grey.600',
                  border: 'none',
                  borderBottom: 'none !important',
                  padding: '2px 4px',
                  textAlign: 'center',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  display: 'table-cell',
                  lineHeight: 1,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden'
                }}
              >
                <TableSortLabel
                  active={sortField === 'starred'}
                  direction={sortField === 'starred' ? sortOrder : 'asc'}
                  onClick={() => handleSort('starred')}
                  sx={{
                    '&.MuiTableSortLabel-root': {
                      color: 'inherit',
                      '&:hover': {
                        color: 'primary.main',
                      },
                      '&.Mui-active': {
                        color: 'primary.main',
                        '& .MuiTableSortLabel-icon': {
                          color: 'primary.main',
                        }
                      }
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <StarBorder sx={{ fontSize: 12, opacity: 0.6 }} />
                  </Box>
                </TableSortLabel>
              </TableCell>
              
              {/* Contact, Subject & Time Column Header - THIRD */}
              <TableCell
                sx={{
                  width: '400px',
                  minWidth: '400px',
                  maxWidth: '400px',
                  bgcolor: darkMode ? 'grey.900' : 'grey.50',
                  fontWeight: 500,
                  fontSize: '0.65rem',
                  textTransform: 'uppercase',
                  color: darkMode ? 'grey.500' : 'grey.600',
                  border: 'none',
                  borderBottom: 'none !important',
                  padding: '2px 4px',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  display: 'table-cell',
                  lineHeight: 1,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                  <Person sx={{ fontSize: 12, opacity: 0.6 }} />
                  Conversation
                </Box>
              </TableCell>
              
              {/* Communication Method Column Header - FOURTH */}
              <TableCell 
                sx={{ 
                  width: '80px',
                  minWidth: '80px',
                  maxWidth: '80px',
                  bgcolor: darkMode ? 'grey.900' : 'grey.50',
                  fontWeight: 500,
                  fontSize: '0.65rem',
                  textTransform: 'uppercase',
                  color: darkMode ? 'grey.500' : 'grey.600',
                  border: 'none',
                  borderBottom: 'none !important',
                  padding: '2px 4px',
                  textAlign: 'center',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  display: 'table-cell',
                  lineHeight: 1,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.3 }}>
                  <IconButton
                    size="small"
                    onClick={(e) => setMethodFilterAnchor(e.currentTarget)}
                    sx={{
                      p: 0.25,
                      color: selectedMethods.length > 0 ? 'primary.main' : 'inherit',
                      '&:hover': {
                        color: 'primary.main',
                        bgcolor: alpha(theme.palette.primary.main, 0.08)
                      }
                    }}
                  >
                    <FilterList sx={{ fontSize: 14 }} />
                  </IconButton>
                  <Typography sx={{ fontSize: '0.65rem', color: selectedMethods.length > 0 ? 'primary.main' : 'inherit' }}>
                    Method
                    {selectedMethods.length > 0 && ` (${selectedMethods.length})`}
                  </Typography>
                </Box>
              </TableCell>
              
              {/* Channel Column Header - FIFTH */}
              <TableCell 
                sx={{ 
                  width: '200px',
                  minWidth: '200px',
                  bgcolor: darkMode ? 'grey.900' : 'grey.50',
                  fontWeight: 500,
                  fontSize: '0.65rem',
                  textTransform: 'uppercase',
                  color: darkMode ? 'grey.500' : 'grey.600',
                  border: 'none',
                  borderBottom: 'none !important',
                  padding: '2px 4px',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  display: 'table-cell',
                  lineHeight: 1,
                  whiteSpace: 'nowrap'
                }}
              >
                <TableSortLabel
                  active={sortField === 'channel_id'}
                  direction={sortField === 'channel_id' ? sortOrder : 'asc'}
                  onClick={() => handleSort('channel_id')}
                  sx={{
                    '&.MuiTableSortLabel-root': {
                      color: 'inherit',
                      '&:hover': {
                        color: 'primary.main',
                      },
                      '&.Mui-active': {
                        color: 'primary.main',
                        '& .MuiTableSortLabel-icon': {
                          color: 'primary.main',
                        }
                      }
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                    <ChatBubbleOutline sx={{ fontSize: 12, opacity: 0.6 }} />
                    Oppy
                  </Box>
                </TableSortLabel>
              </TableCell>
              
              {/* Tags Column Header - SIXTH */}
              <TableCell
                sx={{
                  width: '150px',
                  minWidth: '150px',
                  maxWidth: '150px',
                  bgcolor: darkMode ? 'grey.900' : 'grey.50',
                  fontWeight: 500,
                  fontSize: '0.65rem',
                  textTransform: 'uppercase',
                  color: darkMode ? 'grey.500' : 'grey.600',
                  border: 'none',
                  borderBottom: 'none !important',
                  padding: '2px 4px',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  display: 'table-cell',
                  lineHeight: 1,
                  whiteSpace: 'nowrap'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                  <IconButton
                    size="small"
                    onClick={(e) => setTagsFilterAnchor(e.currentTarget)}
                    sx={{
                      p: 0.25,
                      color: selectedTags.length > 0 ? 'primary.main' : 'inherit',
                      '&:hover': {
                        color: 'primary.main',
                        bgcolor: alpha(theme.palette.primary.main, 0.08)
                      }
                    }}
                  >
                    <FilterList sx={{ fontSize: 14 }} />
                  </IconButton>
                  <Typography sx={{ fontSize: '0.65rem', color: selectedTags.length > 0 ? 'primary.main' : 'inherit' }}>
                    Tags
                    {selectedTags.length > 0 && ` (${selectedTags.length})`}
                  </Typography>
                </Box>
              </TableCell>

              {/* Preview Column Header - SEVENTH */}
              <TableCell 
                sx={{ 
                  width: '400px',
                  minWidth: '400px',
                  bgcolor: darkMode ? 'grey.900' : 'grey.50',
                  fontWeight: 500,
                  fontSize: '0.65rem',
                  textTransform: 'uppercase',
                  color: darkMode ? 'grey.500' : 'grey.600',
                  border: 'none',
                  borderBottom: 'none !important',
                  padding: '2px 4px',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  display: 'table-cell',
                  lineHeight: 1,
                  whiteSpace: 'nowrap'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                  <Visibility sx={{ fontSize: 12, opacity: 0.6 }} />
                  Preview
                </Box>
              </TableCell>
              
              {/* User Messages Count Column Header - EIGHTH */}
              <TableCell 
                sx={{ 
                  width: '60px',
                  minWidth: '60px',
                  bgcolor: darkMode ? 'grey.900' : 'grey.50',
                  fontWeight: 500,
                  fontSize: '0.65rem',
                  textTransform: 'uppercase',
                  color: darkMode ? 'grey.500' : 'grey.600',
                  border: 'none',
                  borderBottom: 'none !important',
                  padding: '2px 4px',
                  textAlign: 'center',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  display: 'table-cell',
                  lineHeight: 1,
                  whiteSpace: 'nowrap'
                }}
              >
                <TableSortLabel
                  active={sortField === 'user_messages_count'}
                  direction={sortField === 'user_messages_count' ? sortOrder : 'asc'}
                  onClick={() => handleSort('user_messages_count')}
                  sx={{
                    '& .MuiTableSortLabel-root': {
                      justifyContent: 'center',
                    },
                    '&.MuiTableSortLabel-root': {
                      color: 'inherit',
                      '&:hover': {
                        color: 'primary.main',
                      },
                      '&.Mui-active': {
                        color: 'primary.main',
                        '& .MuiTableSortLabel-icon': {
                          color: 'primary.main',
                        }
                      }
                    }
                  }}
                >
                  <Box sx={{ width: '100%', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.3 }}>
                    <QuestionAnswer sx={{ fontSize: 12, opacity: 0.6 }} />
                    #
                  </Box>
                </TableSortLabel>
              </TableCell>
              
              {/* Last User Message Column Header - NINTH */}
              <TableCell 
                sx={{ 
                  width: '120px',
                  minWidth: '120px',
                  bgcolor: darkMode ? 'grey.900' : 'grey.50',
                  fontWeight: 500,
                  fontSize: '0.65rem',
                  textTransform: 'uppercase',
                  color: darkMode ? 'grey.500' : 'grey.600',
                  border: 'none',
                  borderBottom: 'none !important',
                  padding: '2px 4px',
                  textAlign: 'right',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  display: 'table-cell',
                  lineHeight: 1,
                  whiteSpace: 'nowrap'
                }}
              >
                <TableSortLabel
                  active={sortField === 'last_user_message_received_at'}
                  direction={sortField === 'last_user_message_received_at' ? sortOrder : 'asc'}
                  onClick={() => handleSort('last_user_message_received_at')}
                  sx={{
                    '& .MuiTableSortLabel-root': {
                      justifyContent: 'flex-end',
                    },
                    '&.MuiTableSortLabel-root': {
                      color: 'inherit',
                      '&:hover': {
                        color: 'primary.main',
                      },
                      '&.Mui-active': {
                        color: 'primary.main',
                        '& .MuiTableSortLabel-icon': {
                          color: 'primary.main',
                        }
                      }
                    }
                  }}
                >
                  <Box sx={{ width: '100%', textAlign: 'right', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.3 }}>
                    <PersonOutline sx={{ fontSize: 12, opacity: 0.6 }} />
                    Last
                  </Box>
                </TableSortLabel>
              </TableCell>
              
              {/* Updated At Column Header - TENTH */}
              <TableCell 
                sx={{ 
                  width: '100px',
                  minWidth: '100px',
                  bgcolor: darkMode ? 'grey.900' : 'grey.50',
                  fontWeight: 500,
                  fontSize: '0.65rem',
                  textTransform: 'uppercase',
                  color: darkMode ? 'grey.500' : 'grey.600',
                  border: 'none',
                  borderBottom: 'none !important',
                  padding: '2px 4px',
                  textAlign: 'right',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  display: 'table-cell',
                  lineHeight: 1,
                  whiteSpace: 'nowrap'
                }}
              >
                <TableSortLabel
                  active={sortField === 'updated_at'}
                  direction={sortField === 'updated_at' ? sortOrder : 'asc'}
                  onClick={() => handleSort('updated_at')}
                  sx={{
                    '& .MuiTableSortLabel-root': {
                      justifyContent: 'flex-end',
                    },
                    '&.MuiTableSortLabel-root': {
                      color: 'inherit',
                      '&:hover': {
                        color: 'primary.main',
                      },
                      '&.Mui-active': {
                        color: 'primary.main',
                        '& .MuiTableSortLabel-icon': {
                          color: 'primary.main',
                        }
                      }
                    }
                  }}
                >
                  <Box sx={{ width: '100%', textAlign: 'right', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.3 }}>
                    <Update sx={{ fontSize: 12, opacity: 0.6 }} />
                    Updated
                  </Box>
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedConversations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No conversations found matching your filters.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : shouldVirtualize ? (
              <TableRow>
                <TableCell colSpan={10} sx={{ p: 0, border: 'none' }}>
                  <Box
                    sx={{
                      height: `${virtualizer.getTotalSize()}px`,
                      width: '100%',
                      position: 'relative',
                    }}
                  >
                    {virtualItems.map((virtualItem) => {
                      const conversation = sortedConversations[virtualItem.index];
                      if (!conversation) {
                        return null;
                      }

                      const isSelected = selectedConversations.includes(conversation.id);
                      const isActive = openConversationId === conversation.id;
                      const itemKey = virtualItem.key;

                      return (
                        <Box
                          key={String(itemKey)}
                          data-index={virtualItem.index}
                          ref={virtualizer.measureElement}
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            transform: `translateY(${virtualItem.start}px)`,
                          }}
                        >
                          <Table
                            sx={{
                              width: isDesktop ? 'max-content' : '100%',
                              minWidth: '100%',
                              tableLayout: isDesktop ? 'fixed' : 'auto',
                              backgroundColor: 'transparent',
                              '& .MuiTableCell-root': {
                                border: 'none !important'
                              }
                            }}
                          >
                            {isDesktop && (
                              <colgroup>
                                <col style={{ width: '48px' }} />
                                <col style={{ width: '48px' }} />
                                <col style={{ width: '300px' }} />
                                <col style={{ width: '80px' }} />
                                <col style={{ width: '200px' }} />
                                <col style={{ width: '220px' }} />
                                <col style={{ width: '400px' }} />
                                <col style={{ width: '60px' }} />
                                <col style={{ width: '120px' }} />
                                <col style={{ width: '100px' }} />
                              </colgroup>
                            )}
                            <TableBody>
                              <ConversationRowItem
                                conversation={conversation}
                                selected={isSelected}
                                active={isActive}
                                onSelect={() => {
                                  handleSelectConversation(conversation.id);
                                  if (handleConversationsListToggleClick) handleConversationsListToggleClick();
                                }}
                                onSelectCheckbox={(event) => onSelectConversation(conversation.id, event)}
                                showCheckbox={showSelectionColumn}
                                disableClick={false}
                                isLoadingFullData={loadingFullDataIds?.has(conversation.id) || false}
                                mobileSelectMode={mobileSelectMode}
                              />
                            </TableBody>
                          </Table>
                        </Box>
                      );
                    })}
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              sortedConversations.map((conversation, index) => {
                const isSelected = selectedConversations.includes(conversation.id);
                const isActive = openConversationId === conversation.id;

                return (
                  <ConversationRowItem
                    key={conversation?.id ?? index}
                    conversation={conversation}
                    selected={isSelected}
                    active={isActive}
                    onSelect={() => {
                      handleSelectConversation(conversation.id);
                      if (handleConversationsListToggleClick) handleConversationsListToggleClick();
                    }}
                    onSelectCheckbox={(event) => onSelectConversation(conversation.id, event)}
                    showCheckbox={showSelectionColumn}
                    disableClick={false}
                    isLoadingFullData={loadingFullDataIds?.has(conversation.id) || false}
                    mobileSelectMode={mobileSelectMode}
                  />
                );
              })
            )}
          </TableBody>
        </Table>
        
          {/* Loading indicator for infinite scroll */}
          {isLoadingMore && hasMore && (
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 3,
              borderTop: `1px solid ${darkMode ? theme.palette.grey[800] : theme.palette.grey[200]}`
            }}>
              <CircularProgress size={30} />
              <Typography variant="body2" sx={{ ml: 2, color: 'text.secondary' }}>
                Loading more conversations...
              </Typography>
            </Box>
          )}

          {/* Load more button for short lists */}
          {hasMore && !isLoadingMore && onLoadMore && (
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 2,
              borderTop: `1px solid ${darkMode ? theme.palette.grey[800] : theme.palette.grey[200]}`
            }}>
              <Button variant="outlined" onClick={() => onLoadMore()}>Load More</Button>
            </Box>
          )}

          {/* End of list indicator */}
          {!hasMore && conversations.length > 0 && (
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 2,
              borderTop: `1px solid ${darkMode ? theme.palette.grey[800] : theme.palette.grey[200]}`
            }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                No more conversations to load
              </Typography>
            </Box>
          )}
      </TableContainer>
      
      {/* Method Filter Menu */}
      <Menu
        anchorEl={methodFilterAnchor}
        open={Boolean(methodFilterAnchor)}
        onClose={() => setMethodFilterAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            mt: 0.5,
            minWidth: 200,
            boxShadow: theme.shadows[8],
          }
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Filter by Method
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Select communication methods
          </Typography>
        </Box>
        <Divider />
        
        <MenuItem
          onClick={() => {
            setSelectedMethods(prev => 
              prev.includes('email') 
                ? prev.filter(m => m !== 'email')
                : [...prev, 'email']
            );
          }}
          sx={{ py: 1 }}
        >
          <ListItemIcon>
            <Checkbox
              size="small"
              checked={selectedMethods.includes('email')}
              sx={{ p: 0 }}
            />
          </ListItemIcon>
          <ListItemText primary="Email" />
        </MenuItem>
        
        <MenuItem
          onClick={() => {
            setSelectedMethods(prev => 
              prev.includes('sms') 
                ? prev.filter(m => m !== 'sms')
                : [...prev, 'sms']
            );
          }}
          sx={{ py: 1 }}
        >
          <ListItemIcon>
            <Checkbox
              size="small"
              checked={selectedMethods.includes('sms')}
              sx={{ p: 0 }}
            />
          </ListItemIcon>
          <ListItemText primary="SMS" />
        </MenuItem>
        
        <MenuItem
          onClick={() => {
            setSelectedMethods(prev => 
              prev.includes('chat') 
                ? prev.filter(m => m !== 'chat')
                : [...prev, 'chat']
            );
          }}
          sx={{ py: 1 }}
        >
          <ListItemIcon>
            <Checkbox
              size="small"
              checked={selectedMethods.includes('chat')}
              sx={{ p: 0 }}
            />
          </ListItemIcon>
          <ListItemText primary="Chat" />
        </MenuItem>
        
        {selectedMethods.length > 0 && (
          <>
            <Divider sx={{ my: 0.5 }} />
            <MenuItem
              onClick={() => setSelectedMethods([])}
              sx={{ py: 1, color: 'text.secondary' }}
            >
              <ListItemIcon>
                <Clear fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Clear filters" />
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Tags Filter Menu */}
      <Menu
        anchorEl={tagsFilterAnchor}
        open={Boolean(tagsFilterAnchor)}
        onClose={() => setTagsFilterAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            mt: 0.5,
            minWidth: 200,
            maxHeight: 400,
            boxShadow: theme.shadows[8],
          }
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Filter by Tags
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {availableTags.length > 0 ? 'Select tags to filter' : 'No tags available'}
          </Typography>
        </Box>
        <Divider />

        {availableTags.length === 0 ? (
          <Box sx={{ px: 2, py: 2 }}>
            <Typography variant="body2" color="text.secondary">
              No tags found in conversations
            </Typography>
          </Box>
        ) : (
          <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
            {availableTags.map((tag) => (
              <MenuItem
                key={tag}
                onClick={() => {
                  const newTags = selectedTags.includes(tag)
                    ? selectedTags.filter(t => t !== tag)
                    : [...selectedTags, tag];
                  handleTagsFilterChange(newTags);
                }}
                sx={{ py: 1 }}
              >
                <ListItemIcon>
                  <Checkbox
                    size="small"
                    checked={selectedTags.includes(tag)}
                    sx={{ p: 0 }}
                  />
                </ListItemIcon>
                <ListItemText primary={tag} />
              </MenuItem>
            ))}
          </Box>
        )}

        {selectedTags.length > 0 && (
          <>
            <Divider />
            <MenuItem
              onClick={() => {
                handleTagsFilterChange([]);
              }}
              sx={{ py: 1, color: 'text.secondary' }}
            >
              <ListItemIcon>
                <Clear fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Clear tags filter" />
            </MenuItem>
          </>
        )}
      </Menu>
    </Box>
  );
};

export default ConversationsTable; 
